/**
 * Décisions de synchronisation - document 02, section 7. **Aucune
 * entrée-sortie.**
 *
 * Toute la logique d'arbitrage vit ici, sans réseau ni base : elle se teste
 * donc sans serveur ni IndexedDB. La couche `stockage/sync.js` ne fait que
 * transporter ce que ce module décide.
 *
 * Modèle volontairement simple (AD-7) : dernière écriture gagne, **par
 * entité**. Jamais par écrasement du lot entier - c'est le garde-fou qui
 * empêche une modification faite sur un appareil d'effacer tout le reste.
 */

/** Les deux magasins synchronisables. `settings` est propre à l'appareil. */
/**
 * Les mouvements se synchronisent comme le reste (feuille de route, lot 7).
 * Le LIVRE, lui, ne se synchronise jamais : il se reconstruit sur chaque
 * appareil depuis les mouvements reçus. Transporter un état dérivé ouvrirait
 * la porte à un livre qui contredit son propre journal.
 */
export const MAGASINS = { profile: 'profiles', quote: 'quotes', movement: 'movements' };

/**
 * Que faire de chaque entité reçue du serveur.
 *
 * @param {object[]} distantes entités renvoyées par /sync/changes
 * @param {Map<string, object>} locales indexées par `${type}:${id}`
 * @returns {{aEcrire: object[], ignorees: object[]}}
 */
export function deciderFusion(distantes, locales) {
  const aEcrire = [];
  const ignorees = [];

  for (const d of distantes) {
    if (!d?.id || !MAGASINS[d.type]) {
      // Une entité d'un type inconnu vient d'une version plus récente du
      // client. On la laisse au serveur plutôt que de la perdre : l'ignorer
      // est réversible, l'écrire dans le mauvais magasin ne l'est pas.
      ignorees.push({ ...d, raison: 'type inconnu' });
      continue;
    }
    const locale = locales.get(`${d.type}:${d.id}`);
    if (!locale) { aEcrire.push(d); continue; }
    // Comparaison lexicographique sur ISO 8601 : équivalente à la comparaison
    // chronologique, sans conversion de fuseau.
    if (d.updatedAt > locale.updatedAt) aEcrire.push(d);
    else ignorees.push({ ...d, raison: 'version locale plus récente ou identique' });
  }

  return { aEcrire, ignorees };
}

/**
 * Entités locales à pousser : celles modifiées depuis la dernière synchro.
 *
 * `lastSyncAt` vide pousse tout, ce qui est le comportement attendu au premier
 * appareil face à un serveur vide.
 *
 * Les entités supprimées sont poussées comme les autres : une suppression est
 * « la dernière écriture », et ne pas la propager la ferait réapparaître au
 * prochain rapprochement.
 */
export function aPousser(entites, lastSyncAt) {
  if (!lastSyncAt) return entites;
  return entites.filter((e) => e.updatedAt > lastSyncAt);
}

/**
 * Nom sous lequel la nature métier d'une entité est mise de côté pendant le
 * transport.
 *
 * COLLISION DE NOMS, et elle coûte cher si on la manque. Le protocole de
 * synchronisation étiquette chaque entité d'un champ `type` (« profile »,
 * « quote », « movement ») dont le serveur se sert pour router. Or un
 * mouvement porte DÉJÀ un champ `type` : sa nature comptable, « achat_eur »
 * ou « vente_eur ».
 *
 * Sans précaution, l'étiquetage écrasait cette nature à l'envoi et le
 * désétiquetage la supprimait à la réception. Les mouvements arrivaient
 * complets - montants, taux, dates - mais sans type : le rejeu les ignorait
 * tous, et l'appareil receveur affichait un livre vide en face d'un journal
 * plein. Aucune erreur nulle part.
 */
const NATURE = 'typeMetier';

/** Étiquette une entité de son type avant l'envoi : le serveur l'exige. */
export function typer(entite, type) {
  const { type: nature, ...reste } = entite;
  return nature === undefined ? { ...reste, type } : { ...reste, type, [NATURE]: nature };
}

/**
 * Retire les champs de transport avant écriture locale et rend sa nature à
 * l'entité. `type` sert au routage, il n'a rien à faire dans l'entité stockée.
 */
export function detyper(entite) {
  const { type, [NATURE]: nature, ...reste } = entite;
  return nature === undefined ? reste : { ...reste, type: nature };
}

/** Message d'état, lisible sans connaître le protocole. */
export function resumer({ recus, ecrits, pousses, refuses }) {
  const bouts = [];
  bouts.push(`${ecrits} entrée${ecrits > 1 ? 's' : ''} reçue${ecrits > 1 ? 's' : ''}`);
  bouts.push(`${pousses} envoyée${pousses > 1 ? 's' : ''}`);
  if (refuses) bouts.push(`${refuses} refusée${refuses > 1 ? 's' : ''} par le serveur`);
  if (recus > ecrits) bouts.push(`${recus - ecrits} ignorée${recus - ecrits > 1 ? 's' : ''}, version locale plus récente`);
  return bouts.join(', ');
}

/**
 * Assemble l'URL d'appel à partir de l'adresse du serveur et d'un chemin.
 *
 * Concaténation et non `new URL(chemin, base)`. La résolution d'URL traite un
 * chemin commençant par « / » comme absolu depuis la RACINE : avec une base
 * « https://hote/api », `new URL('/sync/changes', base)` rend
 * « https://hote/sync/changes » et le préfixe « /api » disparaît sans bruit.
 *
 * Or c'est précisément ce préfixe que le nginx du frontend retire avant de
 * relayer au backend. L'appel tombait donc sur la coquille HTML de
 * l'application, et l'erreur remontait sous la forme « Unexpected token '<' »
 * - un message qui n'oriente vers rien.
 *
 * Le défaut ne s'était pas vu parce que la synchronisation avait été
 * vérifiée en ligne de commande, jamais depuis l'application elle-même.
 */
export function joindreUrl(base, chemin) {
  return `${String(base).replace(/\/+$/, '')}/${String(chemin).replace(/^\/+/, '')}`;
}
